import React, { useState } from "react";
import { Check, Loader2, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import SEO from "../../components/site/SEO";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { T } from "../../lib/testIds";

export default function Contact() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "", honeypot: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent!");
      setSuccess(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <SEO title="Contact — Wonder Washers Pressure Washing" description="Get in touch with Wonder Washers Pressure Washing." canonical="[BOOKING_URL]/contact" />
      <section className="max-w-5xl mx-auto py-20 px-6 grid md:grid-cols-2 gap-10">
        <div>
          <div className="text-xs font-subhead tracking-widest text-[color:var(--ww-red)] uppercase mb-3">Say hi</div>
          <h1 className="font-display uppercase text-5xl md:text-6xl text-[color:var(--ww-navy)] leading-none">Let's get your property on the calendar.</h1>
          <p className="mt-4 text-slate-600 max-w-md">Prefer to talk to a human? Call or text — we'll get right back to you.</p>
          <ul className="mt-8 space-y-4 text-slate-700">
            <li className="flex items-center gap-3"><span className="h-10 w-10 rounded-md bg-[color:var(--ww-navy)] text-[color:var(--ww-gold)] flex items-center justify-center"><Phone className="h-4 w-4" /></span> <a href="tel:[PHONE]">[PHONE]</a></li>
            <li className="flex items-center gap-3"><span className="h-10 w-10 rounded-md bg-[color:var(--ww-navy)] text-[color:var(--ww-gold)] flex items-center justify-center"><Mail className="h-4 w-4" /></span> hello@wonderwashers.com</li>
            <li className="flex items-center gap-3"><span className="h-10 w-10 rounded-md bg-[color:var(--ww-navy)] text-[color:var(--ww-gold)] flex items-center justify-center"><MapPin className="h-4 w-4" /></span> Eugene &amp; the Willamette Valley, Oregon</li>
          </ul>
          <div className="mt-8 rounded-md overflow-hidden border border-slate-200 h-48 bg-slate-100 flex items-center justify-center text-slate-500">
            Map coming soon
          </div>
        </div>
        <div>
          {success ? (
            <div className="ww-card-lg p-10 text-center" data-testid={T.contact.successCard}>
              <div className="mx-auto h-14 w-14 rounded-full bg-[color:var(--ww-red)] text-white flex items-center justify-center mb-6"><Check className="h-7 w-7" /></div>
              <h2 className="font-display uppercase text-3xl text-[color:var(--ww-navy)]">Thanks — we've got your note</h2>
              <p className="mt-3 text-slate-600">We'll respond within the hour, or first thing tomorrow if you sent this after hours.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="ww-card-lg p-6 md:p-8 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">First name *</label>
                  <input data-testid={T.contact.firstName} required autoComplete="given-name" className="ww-input mt-1" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Last name *</label>
                  <input data-testid={T.contact.lastName} required autoComplete="family-name" className="ww-input mt-1" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Email *</label>
                <input data-testid={T.contact.email} required type="email" autoComplete="email" className="ww-input mt-1" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Mobile number *</label>
                <input data-testid={T.contact.phone} required type="tel" inputMode="tel" autoComplete="tel" className="ww-input mt-1" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(541) 555-0117" />
              </div>
              <div>
                <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Message *</label>
                <textarea data-testid={T.contact.message} required className="ww-input mt-1 min-h-[120px]" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
              </div>
              <input tabIndex={-1} className="absolute -left-[9999px] w-0 h-0 opacity-0" value={form.honeypot} onChange={e => setForm(f => ({ ...f, honeypot: e.target.value }))} />
              <div className="text-xs text-slate-500">We'll never sell your info.</div>
              <button data-testid={T.contact.submit} type="submit" disabled={submitting} className="ww-btn-primary inline-flex items-center gap-2">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Send message
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
